import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Building2, MapPin, Mail, ArrowRight, CheckCircle, Palette } from 'lucide-react';
import { ThemeSelector } from '@/components/theme/ThemeSelector';

export function SetupForm() {
  const [websiteName, setWebsiteName] = useState('');
  const [locationServed, setLocationServed] = useState('');
  const [email, setEmail] = useState('');
  const [selectedTheme, setSelectedTheme] = useState('classic');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (websiteName.length < 3 || websiteName.length > 80) {
      toast({
        title: 'Invalid website name',
        description: 'Website name must be between 3 and 80 characters.',
        variant: 'destructive',
      });
      return;
    }

    if (locationServed.length < 3 || locationServed.length > 80) {
      toast({
        title: 'Invalid location',
        description: 'Location must be between 3 and 80 characters.',
        variant: 'destructive',
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: 'Invalid email',
        description: 'Please enter a valid email address.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      // Call edge function to create site and user
      const { data, error } = await supabase.functions.invoke('create-site', {
        body: {
          websiteName: websiteName.trim(),
          locationServed: locationServed.trim(),
          email: email.trim().toLowerCase(),
          loginUrl: window.location.origin + '/admin/login',
          theme: selectedTheme,
        },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      setSuccess(true);
      
      toast({
        title: 'Account created!',
        description: 'Check your email for login credentials.',
      });

    } catch (error: any) {
      console.error('Setup error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Card className="w-full max-w-md mx-auto shadow-elegant animate-scale-in">
        <CardContent className="pt-8 pb-8 text-center">
          <div className="w-16 h-16 bg-estate-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-estate-success" />
          </div>
          <h2 className="font-display text-2xl font-semibold mb-2">Check Your Email!</h2>
          <p className="text-muted-foreground font-body mb-6">
            We've sent your login credentials to <strong>{email}</strong>. 
            Check your inbox (and spam folder) for the email.
          </p>
          <div className="space-y-3">
            <Button
              variant="gold"
              size="lg"
              className="w-full"
              onClick={() => navigate('/admin/login', { state: { email } })}
            >
              Go to Login
            </Button>
            <p className="text-sm text-muted-foreground">
              Didn't receive the email?{' '}
              <button
                onClick={() => navigate('/admin/login', { state: { email, showResend: true } })}
                className="text-accent hover:underline font-medium"
              >
                Resend credentials
              </button>
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto shadow-elegant animate-scale-in">
      <CardHeader className="text-center">
        <CardTitle className="font-display text-2xl">Create Your Real Estate Website</CardTitle>
        <CardDescription className="font-body">
          Get started with a professional real estate website in minutes
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="websiteName" className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              Website Name
            </Label>
            <Input
              id="websiteName"
              value={websiteName}
              onChange={(e) => setWebsiteName(e.target.value)}
              placeholder="Smith Realty Group"
              required
              minLength={3}
              maxLength={80}
            />
            <p className="text-xs text-muted-foreground">3-80 characters</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="locationServed" className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              Location Served
            </Label>
            <Input
              id="locationServed"
              value={locationServed}
              onChange={(e) => setLocationServed(e.target.value)}
              placeholder="South Coast, Massachusetts"
              required
              minLength={3}
              maxLength={80}
            />
            <p className="text-xs text-muted-foreground">3-80 characters</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              Email Address
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
            <p className="text-xs text-muted-foreground">We'll send your login credentials here</p>
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Palette className="h-4 w-4 text-muted-foreground" />
              Color Theme
            </Label>
            <p className="text-xs text-muted-foreground mb-3">
              Choose a visual theme for your town and neighborhood pages
            </p>
            <ThemeSelector
              selectedTheme={selectedTheme}
              onThemeChange={setSelectedTheme}
            />
          </div>

          <Button
            type="submit"
            variant="gold"
            size="xl"
            className="w-full"
            disabled={loading}
          >
            {loading ? (
              'Creating your site...'
            ) : (
              <>
                Go <ArrowRight className="h-5 w-5 ml-2" />
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
